/**
 * The specular that follows the cursor.
 *
 * Real glass answers to light that moves across it. webglass.css positions every
 * surface's highlight at `radial-gradient(... at var(--gx) var(--gy) ...)`, with a
 * resting default of 24%/0% (up and to the left). This module writes those two
 * variables on whichever glass surface the pointer is over, so the highlight
 * tracks the hand. It is the one part of the material a touch screen cannot do,
 * and the reason the desktop site should not just inherit the phone's design.
 *
 * COST CONTROL, because a pointermove handler is the classic way to make a site
 * feel worse while trying to make it feel better:
 *   - ONE delegated listener on the document, passive, never per element.
 *   - Coalesced into a single rAF, so a 1000Hz mouse still writes at most once
 *     per frame, and the write is two custom properties on one element.
 *   - Only ever one element carries the variables; the previous one is cleaned.
 *   - Hover-capable, fine-pointer devices only. Phones and tablets never attach
 *     it, and neither does anyone who asked for reduced motion.
 */

/** The surfaces whose highlight tracks the pointer. Kept to controls and panels
 *  a reader actually points at; the Edition's body copy is print and stays still. */
const LIVE = [
  ".util-btn",
  ".reaction",
  ".image-save",
  ".copy-more-pill",
  ".nav-arrow",
  ".search-btn",
  ".aa-trigger",
  ".theme-trigger",
  ".account-trigger",
  ".insights-chip",
  ".browse-btn",
  ".ed-listen",
  ".ed-share",
  ".gate-card",
  ".insight-card",
  ".view-menu",
  ".mobile-menu",
  ".share-sheet",
].join(",");

let current: HTMLElement | null = null;
let pending: { el: HTMLElement; x: number; y: number } | null = null;
let frame = 0;

function flush(): void {
  frame = 0;
  const next = pending;
  pending = null;
  if (!next) return;
  if (current && current !== next.el) clear(current);
  current = next.el;
  next.el.style.setProperty("--gx", `${next.x}%`);
  next.el.style.setProperty("--gy", `${next.y}%`);
}

function clear(el: HTMLElement): void {
  el.style.removeProperty("--gx");
  el.style.removeProperty("--gy");
}

function onMove(event: PointerEvent): void {
  const target = event.target as Element | null;
  const el = target && typeof target.closest === "function" ? (target.closest(LIVE) as HTMLElement | null) : null;
  if (!el) {
    if (current) {
      clear(current);
      current = null;
    }
    pending = null;
    return;
  }
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  // Clamped so a pointer at the very edge cannot push the highlight off the slab.
  const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
  const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
  pending = { el, x: Math.round(x), y: Math.round(y) };
  if (!frame) frame = requestAnimationFrame(flush);
}

export function initGlassPointer(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  } catch {
    return; // no matchMedia, no way to know it is safe: do nothing
  }
  document.addEventListener("pointermove", onMove, { passive: true });
  // A pointer that leaves the window never fires a final move over a non-target,
  // which would strand the highlight wherever it was abandoned.
  document.addEventListener("pointerleave", () => {
    if (current) clear(current);
    current = null;
    pending = null;
  }, { passive: true });
}
